"use client"

import * as React from "react"
import { InfoPageLayout } from "@/components/info-page-layout"
import { Card, CardContent } from "@/components/ui/card"
import { ChevronDown } from "lucide-react"

const faqCategories = [
  {
    name: "Getting Started",
    questions: [
      {
        q: "How do I join a league?",
        a: "Browse our available leagues, filter by game, day, and region, then click 'Sign Up' on any league with open spots. You'll create an account (if you don't have one) and complete payment to secure your spot."
      },
      {
        q: "What skill level do I need?",
        a: "We have leagues for all skill levels. Each league listing shows the skill requirement (Casual, Intermediate, or Competitive). Be honest about your level—it helps us create balanced, fun matches for everyone."
      },
      {
        q: "Can I join with friends?",
        a: "Yes! When signing up, you can request to be placed on the same team as friends (up to team size limits). Just make sure you all sign up for the same league and note your friends' names during registration."
      },
      {
        q: "What equipment do I need?",
        a: "You need the game, a stable internet connection, and a microphone (most leagues require voice chat). We recommend a headset for the best experience. Specific requirements are listed on each league page."
      }
    ]
  },
  {
    name: "League Rules & Policies",
    questions: [
      {
        q: "What if I can't make a game night?",
        a: "Life happens! Let your team captain know as soon as possible. Most leagues allow 1-2 absences per season without penalty. Teams can use substitute players for missed games. Excessive no-shows may affect future registration priority."
      },
      {
        q: "How are teams formed?",
        a: "Teams are formed based on skill level, region, and any friend requests. Our goal is balanced teams that create competitive, fun matches. Team sizes vary by game (typically 4-6 players)."
      },
      {
        q: "What's the time commitment?",
        a: "Plan for 1-2 hours per week on your scheduled game night, plus optional time for team practice or casual games. Seasons run 8-10 weeks including playoffs."
      },
      {
        q: "Is there a code of conduct?",
        a: "Yes. We require respectful behavior, no hate speech or harassment, and good sportsmanship. This is a recreational league for adults—we're all here to have fun. Violations result in warnings or removal."
      }
    ]
  },
  {
    name: "Payments & Refunds",
    questions: [
      {
        q: "When do I pay?",
        a: "Payment is due at registration to secure your spot. We accept all major credit cards. Your spot isn't confirmed until payment is complete."
      },
      {
        q: "Can I get a refund?",
        a: "Full refunds are available up to 7 days before the season starts. After that, we can offer credit toward a future season if you need to withdraw due to extenuating circumstances. Contact us to discuss."
      },
      {
        q: "Do fees auto-renew?",
        a: "No. We don't do auto-renewals. You'll need to manually register and pay for each new season you want to join. We'll send reminders when registration opens."
      }
    ]
  },
  {
    name: "Technical & Support",
    questions: [
      {
        q: "What games do you support?",
        a: "We currently run leagues for Valorant, Rocket League, Apex Legends, Call of Duty, League of Legends, Overwatch 2, and several others. Check the Leagues page for current offerings. We add new games based on community interest."
      },
      {
        q: "How do I communicate with my team?",
        a: "Each league has a dedicated Discord server with channels for your team. You'll receive an invite link after registration. Discord is required for league participation."
      },
      {
        q: "What if I have technical issues during a match?",
        a: "Contact your team captain or a league moderator on Discord. We have policies for handling disconnections, game crashes, and other technical problems to keep things fair."
      },
      {
        q: "How do I contact support?",
        a: "Email us at support@gamenightleague.com or use the #support channel in Discord. We typically respond within 24 hours, faster during active league nights."
      }
    ]
  }
]

export default function FAQPage() {
  const [openItems, setOpenItems] = React.useState<string[]>([])

  const toggleItem = (id: string) => {
    setOpenItems(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  return (
    <InfoPageLayout 
      title="Frequently Asked Questions"
      subtitle="Everything you need to know about GameNight League."
    >
      <div className="space-y-12 py-8">
        {faqCategories.map((category) => (
          <section key={category.name}>
            <h2 className="text-xl font-bold text-foreground mb-4">{category.name}</h2>
            <div className="space-y-3">
              {category.questions.map((item, i) => {
                const itemId = `${category.name}-${i}`
                const isOpen = openItems.includes(itemId)
                
                return (
                  <Card key={i} className="bg-card border-border">
                    <button
                      className="w-full text-left"
                      onClick={() => toggleItem(itemId)}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between gap-4">
                          <h3 className="font-semibold text-foreground">{item.q}</h3>
                          <ChevronDown 
                            className={`h-5 w-5 text-muted-foreground shrink-0 transition-transform ${
                              isOpen ? "rotate-180" : ""
                            }`} 
                          />
                        </div>
                        {isOpen && (
                          <p className="mt-3 text-sm text-muted-foreground pr-8">
                            {item.a}
                          </p>
                        )}
                      </CardContent>
                    </button>
                  </Card>
                )
              })}
            </div>
          </section>
        ))}

        {/* Contact */}
        <section className="text-center py-8 border-t border-border">
          <h2 className="text-xl font-bold text-foreground mb-2">Still Have Questions?</h2>
          <p className="text-muted-foreground">
            Email us at{" "}
            <a href="mailto:support@gamenightleague.com" className="text-primary hover:underline">
              support@gamenightleague.com
            </a>
          </p>
        </section>
      </div>
    </InfoPageLayout>
  )
}
